<!-- components/UpdateModal.vue -->
<template>
    <transition name="fade">
        <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div class="bg-white rounded-lg shadow-lg w-80 p-6 relative">
                <div class="flex justify-center mb-4">
                    <!-- Spinning Icon -->
                    <svg class="w-12 h-12 text-blue-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none"
                        viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                        </circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                    </svg>
                </div>
                <h2 class="text-xl font-semibold text-center mb-2 text-gray-700">{{ $t('liveUpdate.title') }}</h2>
                <p class="text-center text-gray-600 mb-4">
                    {{ $t('liveUpdate.description') }}
                </p>
                <div class="w-full bg-gray-200 rounded-full h-4">
                    <div class="bg-blue-500 h-4 rounded-full transition-all duration-500"
                        :style="{ width: `${progress}%` }"></div>
                </div>
                <div v-if="isComplete" class="text-center mt-4 text-green-600">
                    {{ $t('liveUpdate.complete') }}
                </div>
            </div>
        </div>
    </transition>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
    show: {
        type: Boolean,
        required: true,
    },
})

const { t: $t } = useI18n()

const emit = defineEmits(['close'])

const progress = ref(0)
const isComplete = ref(false)
let intervalId: number | null = null

// Watch for the 'show' prop to start or stop the progress
watch(
    () => props.show,
    (newVal) => {
        if (newVal) {
            startProgress()
        } else {
            resetProgress()
        }
    }
)

function startProgress() {
    progress.value = 0
    isComplete.value = false

    // Only set up interval on client side
    if (import.meta.client) {
        intervalId = window.setInterval(() => {
            if (progress.value < 100) {
                progress.value += 5
            } else {
                isComplete.value = true
                clearInterval(intervalId!)
                intervalId = null
                // Optionally, emit an event to notify that update is complete
                emit('close')
            }
        }, 1000) // Increment every 1 second
    }
}

function resetProgress() {
    progress.value = 0
    isComplete.value = false
    if (intervalId) {
        clearInterval(intervalId)
        intervalId = null
    }
}

// Clean up on component unmount
onBeforeUnmount(() => {
    if (intervalId) {
        clearInterval(intervalId)
    }
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>